/**
 * Dialog para preview e envio de documento
 * 
 * Exibe preview do documento selecionado com ícone, nome e tamanho,
 * permite renomear antes do envio
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  FileText, 
  FileSpreadsheet, 
  Presentation, 
  FileArchive, 
  File,
  Send,
  X,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

interface SendDocumentDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para fechar o dialog */
  onClose: () => void;
  /** Arquivo de documento selecionado */
  documentFile: File | null;
  /** Callback para enviar o documento */
  onSend: (file: File, customFileName?: string) => Promise<void>;
}

// Limite de 100MB para documentos na Z-API
const MAX_DOCUMENT_SIZE = 100 * 1024 * 1024;

/**
 * Retorna o ícone apropriado baseado no tipo MIME do arquivo
 */
function getDocumentIcon(mimeType: string) {
  // PDF
  if (mimeType.includes('pdf')) {
    return <FileText className="h-16 w-16 text-red-500" />;
  }
  
  // Word
  if (mimeType.includes('word') || mimeType.includes('document') || mimeType.includes('msword')) {
    return <FileText className="h-16 w-16 text-blue-500" />;
  }
  
  // Excel
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) {
    return <FileSpreadsheet className="h-16 w-16 text-green-500" />;
  }
  
  // PowerPoint
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
    return <Presentation className="h-16 w-16 text-orange-500" />;
  }
  
  // Arquivos compactados
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z') || mimeType.includes('archive')) {
    return <FileArchive className="h-16 w-16 text-yellow-500" />;
  }
  
  // Default
  return <File className="h-16 w-16 text-muted-foreground" />;
}

/**
 * Formata o tamanho do arquivo para exibição
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

/**
 * Extrai nome sem extensão e extensão do arquivo
 */
function splitFileName(filename: string): { name: string; extension: string } {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) {
    return { name: filename, extension: '' };
  }
  return {
    name: filename.slice(0, lastDot),
    extension: filename.slice(lastDot),
  };
}

export function SendDocumentDialog({
  open,
  onClose,
  documentFile,
  onSend,
}: SendDocumentDialogProps) {
  const [customFileName, setCustomFileName] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  // Quando o arquivo muda, atualiza o nome customizado
  useEffect(() => {
    if (documentFile) {
      const { name } = splitFileName(documentFile.name);
      setCustomFileName(name);
    }
  }, [documentFile]);
  
  // Handler para enviar (fire-and-forget): fecha modal já,
  // chat trata otimismo/erros via toast e status='failed'.
  const handleSend = () => {
    if (!documentFile) return;

    // Valida tamanho antes de fechar
    if (documentFile.size > MAX_DOCUMENT_SIZE) {
      toast.error('Documento muito grande. O limite é 100MB.');
      return;
    }

    const { extension } = splitFileName(documentFile.name);
    const finalFileName = customFileName.trim()
      ? `${customFileName.trim()}${extension}`
      : documentFile.name;
    const fileToSend = documentFile;

    onClose();
    Promise.resolve(onSend(fileToSend, finalFileName)).catch((error) => {
      console.error('[SendDocumentDialog] background send error:', error);
      toast.error('Erro ao enviar documento');
    });
  };
  
  // Handler para tecla Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  // Previne fechar ao clicar fora enquanto enviando
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isSending) {
      onClose();
    }
  };

  if (!documentFile) return null;

  const { extension } = splitFileName(documentFile.name);
  const isOverSize = documentFile.size > MAX_DOCUMENT_SIZE;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="sm:max-w-md"
        onInteractOutside={(e) => {
          if (isSending) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isSending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Enviar Documento</DialogTitle>
        </DialogHeader>
        
        {/* Preview do documento */}
        <div className="flex flex-col items-center gap-4 py-4">
          {/* Ícone grande */}
          <div className="p-4 bg-muted rounded-lg">
            {getDocumentIcon(documentFile.type)}
          </div>
          
          {/* Info do arquivo */}
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-foreground truncate max-w-[300px]">
              {documentFile.name}
            </p>
            <p className={`text-xs ${isOverSize ? 'text-destructive' : 'text-muted-foreground'}`}>
              {formatFileSize(documentFile.size)}
              {isOverSize && ' (excede limite de 100MB)'}
            </p>
          </div>
        </div>
        
        {/* Campo para renomear */}
        <div className="space-y-2">
          <Label htmlFor="document-name">Nome do arquivo</Label>
          <div className="flex items-center gap-2">
            <Input
              id="document-name"
              value={customFileName}
              onChange={(e) => setCustomFileName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nome do arquivo"
              disabled={isSending}
              className="flex-1"
            />
            <span className="text-sm text-muted-foreground">{extension}</span>
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSending}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || isOverSize || !customFileName.trim()}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Enviar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
